import type { Plugin, Scale } from "chart.js";

export const TICK_COLOR = "rgb(199, 238, 255)";
export const GRID_COLOR = "rgba(70, 130, 180, 0.35)";
export const ACTUAL_COLOR = "#98d8ff";
export const PROJECTION_COLOR = "#e0b93d";
export const SEASON_SHADE = "rgba(121, 109, 206, 0.16)";

export const fmtMonthYear = (ms: number) =>
  new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC"
  });

export const fmtFullDate = (ms: number) =>
  new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  });

// Jan 1 / Jul 1 (or just Jan 1) boundaries inside [min, max], so a numeric
// time axis reads as dates without needing a chart.js date adapter.
export const dateTicks = (min: number, max: number, everyMonths: 6 | 12) => {
  const ticks: { value: number }[] = [];
  const first = new Date(min);
  for (let y = first.getUTCFullYear(); y <= new Date(max).getUTCFullYear(); y++) {
    for (let m = 0; m < 12; m += everyMonths) {
      const value = Date.UTC(y, m, 1);
      if (value >= min && value <= max) ticks.push({ value });
    }
  }
  return ticks;
};

export const dateAxis = (everyMonths: 6 | 12) => ({
  type: "linear" as const,
  ticks: {
    color: TICK_COLOR,
    callback: (value: string | number) =>
      everyMonths === 12
        ? String(new Date(Number(value)).getUTCFullYear())
        : fmtMonthYear(Number(value))
  },
  grid: { color: GRID_COLOR },
  afterBuildTicks: (scale: Scale) => {
    scale.ticks = dateTicks(scale.min, scale.max, everyMonths);
  }
});

// Shades Dec 1 - Apr 30 behind the datasets: the part of the year the kill
// pace runs highest.
export const winterBands: Plugin = {
  id: "winterBands",
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    if (!x || !chartArea) return;
    const firstYear = new Date(x.min).getUTCFullYear() - 1;
    const lastYear = new Date(x.max).getUTCFullYear();
    ctx.save();
    ctx.fillStyle = SEASON_SHADE;
    for (let y = firstYear; y <= lastYear; y++) {
      const left = Math.max(x.getPixelForValue(Date.UTC(y, 11, 1)), chartArea.left);
      const right = Math.min(x.getPixelForValue(Date.UTC(y + 1, 4, 1)), chartArea.right);
      if (right > left) {
        ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
      }
    }
    ctx.restore();
  }
};
